import { Link } from "react-router-dom";
import BlogPostLayout from "./BlogPostLayout";
import { getPostBySlug } from "../../lib/blog";

const HOW_TO = {
  name: "Comment traiter les punaises de lit en Creuse ?",
  description: "Protocole professionnel en 5 étapes pour éradiquer les punaises de lit, avec vapeur sèche 180 °C et biocides certifiés.",
  totalTime: "P28D",
  steps: [
    { name: "Diagnostic sur photo ou visite", text: "Un professionnel Certibiocide confirme l'infestation (punaises vs puces ou autre insecte). Évaluation de l'ampleur." },
    { name: "Préparation du logement", text: "Laver linge et textiles à 60 °C, mettre en sacs plastique scellés ce qui ne peut pas être lavé, aspirer soigneusement les zones-refuges." },
    { name: "1er traitement à J0", text: "Passage vapeur sèche 180 °C sur matelas, sommier, plinthes et coutures. Ajout d'un biocide certifié si nécessaire pour infestations massives." },
    { name: "2ème passage à J+14", text: "Pour tuer les nymphes issues des œufs éclos entre-temps. Vapeur + biocide selon protocole." },
    { name: "Contrôle final à J+28", text: "Vérification de l'absence de traces ou morsures récentes. 2ème passage inclus gratuitement si nécessaire, garantie de résultat." },
  ],
};

export default function PunaisesDeLitGuide() {
  const post = getPostBySlug("punaises-de-lit-reconnaitre-traiter-eviter");
  return (
    <BlogPostLayout post={post} howTo={HOW_TO}>
      <p>
        La <strong>punaise de lit</strong> est l'un des insectes nuisibles les
        plus redoutés en France. Petite (4-7 mm), discrète, active la nuit et
        capable de survivre des mois sans se nourrir, elle peut infester un
        logement, un hôtel ou un gîte en Creuse en quelques semaines. Voici
        comment la reconnaître, la traiter efficacement, et éviter la récidive.
      </p>

      <h2>Comment reconnaître une punaise de lit ?</h2>
      <p>
        L'adulte est de la taille d'un pépin de pomme (4-7 mm), de couleur
        brun-rouge, aplatie sur le dos. Elle ne saute pas (contrairement à la
        puce) et ne vole pas. Les larves, plus claires et plus petites (1-4
        mm), sont souvent invisibles à l'œil nu.
      </p>

      <h3>Les 5 signes d'une infestation</h3>
      <ol>
        <li>
          <strong>Morsures alignées en série de 3</strong> ("petit-déjeuner,
          déjeuner, dîner") sur les bras, jambes, cou. Rougeurs qui
          démangent, apparaissant le matin.
        </li>
        <li>
          <strong>Petites taches noires</strong> sur les coutures du matelas,
          le sommier, les plinthes : ce sont les excréments des punaises.
        </li>
        <li>
          <strong>Taches de sang</strong> sur les draps (punaise écrasée dans
          la nuit).
        </li>
        <li>
          <strong>Odeur douceâtre</strong> caractéristique en cas
          d'infestation avancée (comme de la framboise pourrie).
        </li>
        <li>
          <strong>Petites peaux blanches translucides</strong> (exuvies des
          mues) dans les recoins du matelas.
        </li>
      </ol>

      <div className="callout">
        <strong>Bon à savoir</strong>
        Une punaise de lit peut vivre 6 à 12 mois sans se nourrir, cachée
        dans une fissure. Un logement inoccupé n'est donc pas à l'abri.
      </div>

      <h2>D'où viennent-elles ?</h2>
      <p>
        Contrairement à une idée reçue, la présence de punaises de lit n'a
        rien à voir avec le manque d'hygiène. Les principales voies
        d'introduction sont :
      </p>
      <ul>
        <li>
          <strong>Voyages</strong> — bagages ramenés d'un hôtel, gîte ou
          transport (train, avion) infesté.
        </li>
        <li>
          <strong>Meubles et matelas d'occasion</strong> — cause n°1 des
          infestations résidentielles.
        </li>
        <li>
          <strong>Vêtements achetés</strong> — vide-greniers, friperies non
          contrôlées.
        </li>
        <li>
          <strong>Immeubles collectifs</strong> — propagation via murs
          mitoyens, gaines techniques, plinthes non colmatées.
        </li>
      </ul>

      <h2>Que faire dès la suspicion ?</h2>
      <p>Voici les <strong>4 réflexes immédiats</strong> :</p>
      <ol>
        <li>
          <strong>Ne changez pas de chambre.</strong> Vous risquez de
          propager les punaises dans le reste du logement.
        </li>
        <li>
          <strong>N'utilisez PAS de bombe insecticide grand public.</strong>{" "}
          Ces produits dispersent les punaises et rendent le traitement
          professionnel plus difficile.
        </li>
        <li>
          <strong>Lavez linge et textiles à 60 °C minimum.</strong> Mettez en
          sacs plastique scellés ce qui ne peut pas être lavé (peluches, sacs
          à main, chaussures) et gardez-les fermés 15 jours.
        </li>
        <li>
          <strong>Contactez un professionnel Certibiocide.</strong> Plus le
          diagnostic est précoce, plus le traitement est court et efficace.
        </li>
      </ol>

      <h2>Le traitement professionnel efficace</h2>
      <p>
        Chez K'REUSE EXTÉRIEUR, nous privilégions la{" "}
        <strong>vapeur sèche 180 °C</strong>. Elle tue instantanément adultes,
        larves et œufs, sans aucun produit chimique, sans éviction du
        logement. La vapeur pénètre dans les coutures, plinthes et fissures
        inaccessibles aux insecticides.
      </p>
      <p>
        Pour les infestations massives, nous complétons par un{" "}
        <strong>traitement biocide certifié</strong> en 2 ou 3 passages
        espacés de 14 jours, afin de tuer les nymphes issues des œufs qui
        éclosent après le premier passage. Contrôle final à J+28. Voir notre
        page dédiée{" "}
        <Link to="/punaises-de-lit-creuse">
          traitement punaises de lit en Creuse
        </Link>{" "}
        pour les tarifs et le protocole complet.
      </p>

      <h2>Combien ça coûte en Creuse ?</h2>
      <ul>
        <li>Studio / petite chambre : <strong>150-250 €</strong></li>
        <li>Appartement T2-T3 : <strong>250-450 €</strong></li>
        <li>Maison ou gîte : <strong>400-700 €</strong></li>
      </ul>
      <p>
        Le tarif inclut le diagnostic, le traitement complet, un 2ème passage
        gratuit si nécessaire, et le contrôle final. Aucun coût caché.
      </p>

      <h2>Comment éviter la récidive ?</h2>
      <ul>
        <li>
          <strong>Après un voyage</strong> — inspecter les bagages avant de
          les rentrer, laver textiles à 60 °C.
        </li>
        <li>
          <strong>Éviter</strong> les matelas et meubles d'occasion non
          vérifiés (surtout les tissus).
        </li>
        <li>
          <strong>Vérifier</strong> régulièrement coutures du matelas et
          plinthes derrière la tête de lit.
        </li>
        <li>
          <strong>En logement collectif</strong> — colmater les passages de
          câbles et prises électriques avec les voisins.
        </li>
      </ul>

      <div className="callout-warn">
        <strong>Vous êtes hébergeur en Creuse ?</strong>
        Chambres d'hôtes, gîtes, hôtels : un contrat annuel de prévention avec
        contrôles réguliers évite les avis négatifs catastrophiques sur
        Google/Booking et protège votre réputation. Contactez-nous pour un
        devis contractuel.
      </div>

      <h2>Pour aller plus loin</h2>
      <p>
        Consultez également notre{" "}
        <Link to="/desinsectisation-creuse">
          page désinsectisation Creuse
        </Link>{" "}
        et notre{" "}
        <Link to="/entreprise-nuisibles-creuse">
          entreprise anti-nuisibles Creuse
        </Link>{" "}
        pour tous nos services.
      </p>
    </BlogPostLayout>
  );
}
